import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  ContentChild,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { ADMIN_TABLE, AdminTableSortHost } from './admin-table.token';
import { AdminPaginatorComponent } from './paginator.component';
import { AdminTableIconComponent } from './table-icon.component';
import {
  AdminTableEmptyDirective,
  AdminTableHeaderDirective,
  AdminTableRowDirective,
} from './table-templates.directive';
import { clampPage, pageSlice, SortDir, sortRows, TablePage } from './table.util';

/**
 * Admin data table (replaces PrimeNG's p-table). Two modes:
 * - client (default): sorts and pages `rows` itself;
 * - `lazy`: shows `rows` as given (one server page) and emits `pageChange`
 *   when the user pages; the parent fetches and passes `page` / `total` back.
 * `pageChange` fires only on user action, never on init, so lazy parents load
 * their first page themselves. Header cells sort with `th[adminSort]` (client mode).
 */
@Component({
  selector: 'admin-table',
  standalone: true,
  imports: [AdminPaginatorComponent, AdminTableIconComponent, NgTemplateOutlet],
  providers: [{ provide: ADMIN_TABLE, useExisting: forwardRef(() => AdminTableComponent) }],
  // Unencapsulated: the header/row templates belong to the parent component's view.
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./admin-table.component.scss'],
  template: `
    <div class="admin-table" [class.admin-table--hover]="rowHover">
      <div class="admin-table__scroll">
        <table>
          <thead>
            @if (header) {
              <ng-container *ngTemplateOutlet="header.template"></ng-container>
            }
          </thead>
          <tbody>
            @for (row of view; track $index) {
              @if (body) {
                <ng-container
                  *ngTemplateOutlet="body.template; context: { $implicit: row, index: $index }"
                ></ng-container>
              }
            } @empty {
              @if (empty) {
                <ng-container *ngTemplateOutlet="empty.template"></ng-container>
              }
            }
          </tbody>
        </table>
      </div>
      @if (paginator) {
        <admin-paginator
          [page]="currentPage"
          [pageSize]="currentSize"
          [total]="totalRecords"
          [pageSizeOptions]="pageSizeOptions"
          [itemLabel]="itemLabel"
          (pageChange)="onPage($event)"
        ></admin-paginator>
      }
      @if (loading) {
        <div class="admin-table__mask">
          <admin-table-icon class="admin-table__spinner" name="spinner"></admin-table-icon>
        </div>
      }
    </div>
  `,
})
export class AdminTableComponent<T = unknown> implements OnChanges, AdminTableSortHost {
  @Input() rows: readonly T[] | null | undefined = [];
  @Input() loading = false;
  @Input() paginator = true;
  @Input() pageSize = 50;
  @Input() pageSizeOptions: number[] = [];
  /** Noun for the "Showing x to y of n …" line; omitted when empty. */
  @Input() itemLabel = '';
  @Input() rowHover = false;
  @Input() sortField?: string;
  @Input() sortDir: SortDir = 'asc';
  /** Server paging: `rows` is one page; `total` and `page` (1-based) come from the parent. */
  @Input() lazy = false;
  @Input() total = 0;
  @Input() page = 1;
  @Output() readonly pageChange = new EventEmitter<TablePage>();

  @ContentChild(AdminTableHeaderDirective) header?: AdminTableHeaderDirective;
  @ContentChild(AdminTableRowDirective) body?: AdminTableRowDirective<T>;
  @ContentChild(AdminTableEmptyDirective) empty?: AdminTableEmptyDirective;

  currentPage = 1;
  currentSize = 50;
  view: T[] = [];

  get totalRecords(): number {
    return this.lazy ? this.total : this.safeRows.length;
  }

  /** Anything that isn't an array (a bad API response, null) renders as no rows. */
  private get safeRows(): readonly T[] {
    return Array.isArray(this.rows) ? this.rows : [];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['page']) this.currentPage = this.page;
    if (changes['pageSize']) this.currentSize = this.pageSize;
    this.refresh();
  }

  sortBy(field: string): void {
    this.sortDir = this.sortField === field && this.sortDir === 'asc' ? 'desc' : 'asc';
    this.sortField = field;
    this.refresh();
  }

  onPage(event: TablePage): void {
    this.currentPage = event.page;
    this.currentSize = event.pageSize;
    if (!this.lazy) this.refresh();
    this.pageChange.emit(event);
  }

  private refresh(): void {
    const rows = this.safeRows;
    if (this.lazy) {
      // One server page; like PrimeNG, never render more than a page even if the server sends more.
      this.view = this.paginator ? rows.slice(0, this.currentSize) : [...rows];
      return;
    }
    this.currentPage = clampPage(this.currentPage, rows.length, this.currentSize);
    const sorted = sortRows(rows, this.sortField, this.sortDir);
    this.view = this.paginator ? pageSlice(sorted, this.currentPage, this.currentSize) : sorted;
  }
}
